// M004-F0646 / F0647 - inline remove confirm (design §3.5): before/after
// mini-strips from the coverage READ and PREVIEW endpoints, the impact line
// from `POST /assignments/impact`, the "Grandfather existing bookings"
// checkbox (default OFF). If the impact read fails, Remove is BLOCKED inline
// (2026-09-04 R4 idiom) - an unreadable impact is never a silent zero.
import React from 'react';
import { DAY_LABELS, PATTERN_CSS, formatMinor, styleForPlan } from './planModel';

const minutesOf = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
};

export const MiniStrip = ({ coverage, styles, testId }) => {
  if (!coverage) return <div className="mpp-muted" data-testid={testId} data-ready="false">Loading&hellip;</div>;
  const slotsByDay = new Map();
  const times = new Set();
  (coverage.days || []).forEach((day) => {
    const slots = new Map();
    (day.slots || []).forEach((slot) => { slots.set(slot.start, slot); times.add(slot.start); });
    slotsByDay.set(Number(day.iso_weekday), slots);
  });
  const rows = [...times].sort((a, b) => minutesOf(a) - minutesOf(b)).filter((t) => minutesOf(t) >= 6 * 60);
  return (
    <div className="mpp-mini" data-testid={testId} data-ready="true" aria-hidden="true">
      {DAY_LABELS.map((label) => <div className="mpp-mini-head" key={label}>{label.charAt(0)}</div>)}
      {rows.map((start) => DAY_LABELS.map((label, index) => {
        const slot = slotsByDay.get(index + 1)?.get(start);
        const st = slot?.winner ? styleForPlan(styles, slot.winner.plan_id) : null;
        return (
          <div
            key={`${start}-${label}`}
            className="mpp-mini-cell"
            data-plan-id={slot?.winner?.plan_id ?? ''}
            style={st ? { backgroundColor: st.color, backgroundImage: st.pattern === 'solid' ? undefined : PATTERN_CSS[st.pattern] } : undefined}
          />
        );
      }))}
    </div>
  );
};

export const impactSentence = (impact) => {
  const n = impact.to_reprice;
  const invoiced = impact.refused_billed;
  const delta = impact.amount_delta_minor;
  const heldText = impact.held ? `, ${impact.held} held` : '';
  if (!n) {
    return `No upcoming bookings change price${heldText}; ${invoiced} already invoiced (cannot change).`;
  }
  const direction = delta < 0 ? 'cheaper' : 'dearer';
  const amount = delta === 0 ? 'no change in total' : `about ${formatMinor(Math.abs(delta))} ${direction}`;
  return `Reprices ${n} upcoming booking${n === 1 ? '' : 's'}, ${amount}${heldText}; ${invoiced} already invoiced stay unchanged.`;
};

const RemoveConfirm = ({
  name, roomName, beforeCoverage, afterCoverage, styles, impact, impactError, impactLoading,
  grandfather, onGrandfather, onConfirm, onCancel,
}) => {
  const blocked = Boolean(impactError) || impactLoading || !impact;
  return (
    <div className="mpp-confirm" role="group" aria-label={`Remove ${name}?`} data-testid="plan-remove-confirm">
      <h4>Remove {name}?</h4>
      <div className="mpp-ba">
        <div><small>Before ({roomName})</small><MiniStrip coverage={beforeCoverage} styles={styles} testId="remove-strip-before" /></div>
        <div><small>After</small><MiniStrip coverage={afterCoverage} styles={styles} testId="remove-strip-after" /></div>
      </div>
      {impactLoading ? <div className="mpp-muted" data-testid="remove-impact-loading">Checking the impact on upcoming bookings&hellip;</div> : null}
      {impactError ? <div className="mpp-error" role="alert" data-testid="remove-impact-error">{impactError}</div> : null}
      {impact ? <div className="mpp-muted" data-testid="remove-impact-line">{impactSentence(impact)}</div> : null}
      <label className="mpp-check">
        <input type="checkbox" checked={grandfather} onChange={(event) => onGrandfather(event.target.checked)} data-testid="remove-grandfather" />
        {' '}Grandfather existing bookings
      </label>
      <div className="mpp-row">
        <button type="button" className="mpp-btn mpp-btn-danger mpp-btn-sm" disabled={blocked} onClick={onConfirm} data-testid="remove-confirm-ok">Remove</button>
        <button type="button" className="mpp-btn mpp-btn-outline mpp-btn-sm" onClick={onCancel} data-testid="remove-confirm-keep">Keep</button>
      </div>
    </div>
  );
};

export default RemoveConfirm;
